import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TermsModal from "../components/TermsModal"; // Import the modal
import "../styles/LoginPage.css"; // Your existing styles

const API_URL = process.env.REACT_APP_BACKEND_URL;
const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const TERMS_STORAGE_KEY = "termsAccepted_v1"; // Key for localStorage persistence

const LoginPage = () => {
  const navigate = useNavigate();
  const googleSignInDivRef = useRef(null); // Ref for the Google button container

  // --- State Management ---
  const initialTermsAccepted =
    localStorage.getItem(TERMS_STORAGE_KEY) === "true";
  const [isModalOpen, setIsModalOpen] = useState(!initialTermsAccepted);
  const [termsAccepted, setTermsAccepted] = useState(initialTermsAccepted);
  const [isGoogleInitialized, setIsGoogleInitialized] = useState(false); // Track Google library init status

  // --- Google Sign-In Handler (Callback) ---
  const handleCredentialResponse = useCallback(
    (response) => {
      // Still ensure terms are accepted before processing
      if (!termsAccepted) {
        console.warn("Login attempt before accepting terms.");
        alert("Please accept the Terms of Service before signing in.");
        return;
      }
      const idToken = response.credential;

      // --- Fetch to backend ---
      fetch(`${API_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: idToken }),
      })
        .then((response) => {
          if (!response.ok) {
            // Try to parse error detail from backend, otherwise use statusText
            return response
              .json()
              .then((errData) => {
                throw new Error(
                  errData.detail ||
                    `Authentication failed: ${response.statusText} (Status: ${response.status})`
                );
              })
              .catch(() => { // If .json() fails (e.g., response is not JSON)
                throw new Error(
                  `Authentication failed: ${response.statusText} (Status: ${response.status})`
                );
              });
          }
          return response.json();
        })
        .then((data) => { // data is the response from your backend /auth/google
          // NEW LOGIC STARTS HERE:
          // Check if the backend response contains the necessary fields from the modified backend
          if (data.token && data.email && data.access !== undefined && data.is_admin !== undefined) {
            localStorage.setItem(
              "user",
              JSON.stringify({
                email: data.email,
                token: data.token,
                access: data.access,     // Use data.access directly from backend
                is_admin: data.is_admin, // Use data.is_admin directly from backend
              })
            );
            
            // Optional: redirect based on admin status after successful login
            if (data.is_admin) { 
              navigate("/admin"); // Or your main admin dashboard route
            } else {
              navigate("/");     // Or your main non-admin dashboard route
            }
          } else {
            // If backend response is missing expected fields, even if status was 200
            throw new Error(
              data.detail || "Authentication response from server was incomplete or invalid."
            );
          }
          // NEW LOGIC ENDS HERE.
        })
        .catch((error) => {
          console.error("Error during authentication process:", error);
          alert(
            `Sign-in failed: ${error.message}. Please try again or contact support.`
          );
        });
      // --- End Fetch ---
    },
    [navigate, termsAccepted, API_URL] // Added API_URL to dependencies as it's used inside
  );

  // --- Effect 1: Initialize Google Library ---
  useEffect(() => {
    // Check if the Google library object exists
    if (window.google && window.google.accounts && window.google.accounts.id) {
      console.log("Initializing Google Identity Services...");
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse, // Pass the memoized callback
        });
        setIsGoogleInitialized(true); // Set flag indicating initialization is done
        console.log("Google Identity Services Initialized.");
      } catch (error) {
        console.error("Error initializing Google Identity Services:", error);
        setIsGoogleInitialized(false); // Ensure flag is false on error
        // Optionally alert the user
        // alert("Could not initialize Sign-In functionality. Please refresh.");
      }
    } else {
      // Library not loaded yet, might need a check/retry or ensure script loads first
      console.warn(
        "Google Identity Services library not found on initial load."
      );
      // You might need to ensure the script in index.html loads reliably before this runs
    }
    // Run this effect only once on mount, dependencies ensure callback is up-to-date
  }, [clientId, handleCredentialResponse]);

  // --- Effect 2: Render Google Button ---
  useEffect(() => {
    // Render the button only if Google is initialized AND terms are accepted
    if (isGoogleInitialized && termsAccepted) {
      console.log("Rendering Google Sign-In button...");
      if (googleSignInDivRef.current) {
        // Clear the container first to avoid duplicate buttons on potential re-renders
        googleSignInDivRef.current.innerHTML = "";
        try {
          window.google.accounts.id.renderButton(googleSignInDivRef.current, {
            theme: "filled_blue",
            size: "large",
            type: "standard",
            width: 280,
            logo_alignment: "center",
          });
          console.log("Google Sign-In button rendered.");
          // Optional: Auto-prompt after terms are accepted and button is rendered
          // window.google.accounts.id.prompt();
        } catch (error) {
          console.error("Error rendering Google Sign-In button:", error);
          // Optionally alert the user
          // alert("Could not display the Sign-In button.");
        }
      } else {
        console.error("Target div for Google Sign-In button not found.");
      }
    } else {
      // Log why the button isn't rendering yet
      if (!isGoogleInitialized)
        console.log("Button render waiting for Google initialization...");
      if (!termsAccepted)
        console.log("Button render waiting for terms acceptance...");
    }
    // This effect depends on initialization status and terms acceptance
  }, [isGoogleInitialized, termsAccepted]);

  // --- Modal Handlers ---
  const handleAcceptTerms = () => {
    localStorage.setItem(TERMS_STORAGE_KEY, "true"); // Persist acceptance
    setTermsAccepted(true); // Update state
    setIsModalOpen(false); // Close modal
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // --- Render Logic (JSX remains the same as previous version) ---
  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">
          Welcome to <span className="gradient-text">Cloudfresh's</span> License
          Manager
        </h1>

        <div className="google-signin-container">
          {termsAccepted ? (
            <>
              <p className="login-subtitle">Sign in with your Google account</p>
              {/* Render placeholder until button is ready, or just the div */}
              <div id="googleSignInDiv" ref={googleSignInDivRef}>
                {/* Optional: Add a loading indicator here while waiting for renderButton */}
                {!isGoogleInitialized && (
                  <div className="google-signin-placeholder">
                    Initializing Sign-In...
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="login-subtitle">
                Please accept the Terms of Service to sign in.
              </p>
              <div className="google-signin-placeholder"></div>
            </>
          )}
        </div>

        {/* Original Terms Text */}
        <div className="terms-text">
          <p>By signing in, you agree to our</p>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
          >
            Terms of Service
          </a>
          {" and "}
          <a
            href="https://cloudfresh.com/en/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>{" "}
      {/* End of login-card */}
      {/* Modal Component */}
      <TermsModal
        isOpen={isModalOpen}
        onAccept={handleAcceptTerms}
        onClose={handleCloseModal}
      />
    </div> // End of login-container
  );
};

export default LoginPage;
